import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDroppable } from '@dnd-kit/core';
import { Plus, Minus, CornersOut, User, MapPin, Package, X, Prohibit } from '@phosphor-icons/react';
import { useIdea } from '../../context/IdeaContext';
import { IdeaCard, EntityType, BeatKind } from '../../types';
import { BoardGeometry } from '../../lib/ideaService';
import { BEAT_COLORS, readableTextOn } from '../../lib/beatColors';
import { outlineLaneAt, outlineDefaultLength, formatPage } from './OutlineStrip';

// ═══════════════════════════════════════════════════════════════════════════════
// BEAT BOARD - free canvas of beats with connectors (Final Draft Beat Board style)
// Beats are idea cards; geometry lives in idea_board_items, arrows in idea_beat_links.
// Dragging a beat onto the outline strip places it there (FD's "drag beats to outline").
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_SIZE = { width: 240, height: 120 };
const MIN_SIZE = { width: 140, height: 72 };
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;
const GRID = 20;
const DRAG_THRESHOLD = 3;

type View = { x: number; y: number; zoom: number };
type Point = { x: number; y: number };

type Interaction =
    | { kind: 'pan'; start: Point; origin: View }
    | { kind: 'move'; cardId: string; start: Point; origin: BoardGeometry }
    | { kind: 'resize'; cardId: string; start: Point; origin: BoardGeometry }
    | { kind: 'connect'; fromCardId: string };

const snap = (value: number) => Math.round(value / GRID) * GRID;
const clampZoom = (zoom: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));

const center = (g: BoardGeometry): Point => ({ x: g.x + g.width / 2, y: g.y + g.height / 2 });

// Where the line from a rect's center toward `toward` leaves the rect
const edgePoint = (g: BoardGeometry, toward: Point): Point => {
    const c = center(g);
    const dx = toward.x - c.x;
    const dy = toward.y - c.y;
    if (dx === 0 && dy === 0) return c;
    const t = Math.min(
        dx !== 0 ? (g.width / 2) / Math.abs(dx) : Infinity,
        dy !== 0 ? (g.height / 2) / Math.abs(dy) : Infinity
    );
    return { x: c.x + dx * t, y: c.y + dy * t };
};

const viewStorageKey = (ideaId: string) => `ezart.beatboard.view.${ideaId}`;

const readStoredView = (ideaId: string): View | null => {
    try {
        const parsed = JSON.parse(localStorage.getItem(viewStorageKey(ideaId)) || 'null');
        if (parsed && Number.isFinite(parsed.x) && Number.isFinite(parsed.y) && Number.isFinite(parsed.zoom)) {
            return { x: parsed.x, y: parsed.y, zoom: clampZoom(parsed.zoom) };
        }
    } catch {
        // Unavailable storage just means the board opens at the default view
    }
    return null;
};

interface BeatBoardProps {
    canEdit: boolean;
    onOpenCard: (cardId: string) => void;
}

export const BeatBoard: React.FC<BeatBoardProps> = ({ canEdit, onOpenCard }) => {
    const {
        currentIdea, cards, boardItems, beatLinks, workspaceState,
        selectCard, createCard, updateCard, saveBoardItem, createBeatLink, deleteBeatLink,
        placeCard, updateInstance
    } = useIdea();

    const containerRef = useRef<HTMLDivElement>(null);
    const [view, setView] = useState<View>({ x: 0, y: 0, zoom: 1 });
    const [interaction, setInteraction] = useState<Interaction | null>(null);
    const [draft, setDraft] = useState<{ cardId: string; geometry: BoardGeometry } | null>(null);
    const [connectPointer, setConnectPointer] = useState<Point | null>(null);
    const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
    // A beat being dragged over the outline strip: where it would land
    const [outlineDrop, setOutlineDrop] = useState<{ lane: 'macro' | 'micro'; page: number; pointer: Point } | null>(null);
    // Read by the window pointer handlers without re-subscribing on every move
    const movedRef = useRef(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const hoveredLaneRef = useRef<HTMLElement | null>(null);

    const setHoveredLane = (element: HTMLElement | null) => {
        if (hoveredLaneRef.current === element) return;
        if (hoveredLaneRef.current) delete hoveredLaneRef.current.dataset.dropActive;
        if (element) element.dataset.dropActive = 'true';
        hoveredLaneRef.current = element;
    };

    const ideaId = currentIdea?.id;
    const selectedCardId = workspaceState.selectedCardId;

    // Restore the last view for this idea
    useEffect(() => {
        if (!ideaId) return;
        setView(readStoredView(ideaId) ?? { x: 0, y: 0, zoom: 1 });
    }, [ideaId]);

    useEffect(() => {
        if (!ideaId) return;
        try {
            localStorage.setItem(viewStorageKey(ideaId), JSON.stringify(view));
        } catch {
            // Storage unavailable; the view simply isn't remembered
        }
    }, [ideaId, view]);

    // Saved geometry, with unplaced beats laid out in a grid below the placed ones
    const geometryByCard = useMemo(() => {
        const map = new Map<string, BoardGeometry & { z_index: number }>();
        for (const item of boardItems) {
            map.set(item.card_id, { x: item.x, y: item.y, width: item.width, height: item.height, z_index: item.z_index });
        }
        const placedBottom = boardItems.reduce((max, item) => Math.max(max, item.y + item.height), 0);
        const startY = boardItems.length ? placedBottom + GRID * 3 : GRID * 2;
        let slot = 0;
        for (const card of cards) {
            if (map.has(card.id)) continue;
            map.set(card.id, {
                x: GRID * 2 + (slot % 4) * (DEFAULT_SIZE.width + GRID * 2),
                y: startY + Math.floor(slot / 4) * (DEFAULT_SIZE.height + GRID * 2),
                ...DEFAULT_SIZE,
                z_index: 0
            });
            slot++;
        }
        return map;
    }, [cards, boardItems]);

    const geometryOf = useCallback((cardId: string): BoardGeometry | undefined =>
        draft?.cardId === cardId ? draft.geometry : geometryByCard.get(cardId), [draft, geometryByCard]);

    const maxZ = useMemo(() => boardItems.reduce((max, item) => Math.max(max, item.z_index), 0), [boardItems]);

    // Draw order: z_index, with the selected or dragged beat on top
    const orderedCards = useMemo(() => {
        const activeId = draft?.cardId ?? selectedCardId;
        return [...cards].sort((a, b) => {
            if (a.id === activeId) return 1;
            if (b.id === activeId) return -1;
            return (geometryByCard.get(a.id)?.z_index ?? 0) - (geometryByCard.get(b.id)?.z_index ?? 0);
        });
    }, [cards, geometryByCard, draft?.cardId, selectedCardId]);

    const toWorld = useCallback((clientX: number, clientY: number): Point => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        return { x: (clientX - rect.left - view.x) / view.zoom, y: (clientY - rect.top - view.y) / view.zoom };
    }, [view]);

    const zoomAt = useCallback((nextZoom: number, screenPoint?: Point) => {
        const rect = containerRef.current?.getBoundingClientRect();
        setView(prev => {
            const zoom = clampZoom(nextZoom);
            const anchor = screenPoint ?? (rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 });
            const scale = zoom / prev.zoom;
            return { zoom, x: anchor.x - (anchor.x - prev.x) * scale, y: anchor.y - (anchor.y - prev.y) * scale };
        });
    }, []);

    const fitToBeats = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        const all = [...geometryByCard.values()];
        if (!rect || all.length === 0) {
            setView({ x: 0, y: 0, zoom: 1 });
            return;
        }
        const minX = Math.min(...all.map(g => g.x));
        const minY = Math.min(...all.map(g => g.y));
        const maxX = Math.max(...all.map(g => g.x + g.width));
        const maxY = Math.max(...all.map(g => g.y + g.height));
        const pad = 48;
        const zoom = clampZoom(Math.min((rect.width - pad * 2) / (maxX - minX), (rect.height - pad * 2) / (maxY - minY), 1));
        setView({
            zoom,
            x: (rect.width - (maxX - minX) * zoom) / 2 - minX * zoom,
            y: (rect.height - (maxY - minY) * zoom) / 2 - minY * zoom
        });
    }, [geometryByCard]);

    // Wheel pans; Ctrl/Cmd + wheel (and trackpad pinch) zooms around the pointer
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            if (event.ctrlKey || event.metaKey) {
                const rect = el.getBoundingClientRect();
                setView(prev => {
                    const zoom = clampZoom(prev.zoom * Math.exp(-event.deltaY * 0.0025));
                    const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
                    const scale = zoom / prev.zoom;
                    return { zoom, x: anchor.x - (anchor.x - prev.x) * scale, y: anchor.y - (anchor.y - prev.y) * scale };
                });
            } else {
                setView(prev => ({ ...prev, x: prev.x - event.deltaX, y: prev.y - event.deltaY }));
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    // Pointer tracking for the active interaction
    useEffect(() => {
        if (!interaction) return;

        const onMove = (event: PointerEvent) => {
            if (interaction.kind === 'pan') {
                const dx = event.clientX - interaction.start.x;
                const dy = event.clientY - interaction.start.y;
                if (!movedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
                movedRef.current = true;
                setView({ ...interaction.origin, x: interaction.origin.x + dx, y: interaction.origin.y + dy });
            } else if (interaction.kind === 'move') {
                const dx = (event.clientX - interaction.start.x) / view.zoom;
                const dy = (event.clientY - interaction.start.y) / view.zoom;
                if (!movedRef.current && Math.hypot(dx * view.zoom, dy * view.zoom) < DRAG_THRESHOLD) return;
                movedRef.current = true;
                setDraft({ cardId: interaction.cardId, geometry: { ...interaction.origin, x: interaction.origin.x + dx, y: interaction.origin.y + dy } });
                // Over the outline strip? (the board clips the beat, so hit-testing reaches the lane)
                const target = outlineLaneAt(event.clientX, event.clientY);
                setHoveredLane(target?.element ?? null);
                setOutlineDrop(target ? { lane: target.lane, page: target.page, pointer: { x: event.clientX, y: event.clientY } } : null);
            } else if (interaction.kind === 'resize') {
                const dx = (event.clientX - interaction.start.x) / view.zoom;
                const dy = (event.clientY - interaction.start.y) / view.zoom;
                setDraft({
                    cardId: interaction.cardId,
                    geometry: {
                        ...interaction.origin,
                        width: Math.max(MIN_SIZE.width, interaction.origin.width + dx),
                        height: Math.max(MIN_SIZE.height, interaction.origin.height + dy)
                    }
                });
            } else if (interaction.kind === 'connect') {
                setConnectPointer(toWorld(event.clientX, event.clientY));
            }
        };

        const onUp = (event: PointerEvent) => {
            const pending = draftRef.current;
            const outlineTarget = interaction.kind === 'move' && movedRef.current
                ? outlineLaneAt(event.clientX, event.clientY)
                : null;
            if (interaction.kind === 'pan' && !movedRef.current) {
                selectCard(null);
                setSelectedLinkId(null);
            } else if (interaction.kind === 'move' && outlineTarget) {
                // Dropped on the outline: place it there; the beat keeps its board position
                const cardId = interaction.cardId;
                const targetPages = currentIdea?.outline_target_pages ?? 120;
                placeCard(cardId, outlineTarget.lane).then(instance => {
                    if (instance) {
                        updateInstance(instance.id, {
                            page_goal: outlineTarget.page,
                            page_length: outlineDefaultLength(outlineTarget.lane, targetPages)
                        });
                    }
                });
            } else if ((interaction.kind === 'move' && movedRef.current) || interaction.kind === 'resize') {
                if (pending?.cardId === interaction.cardId) {
                    const g = pending.geometry;
                    saveBoardItem(interaction.cardId, {
                        x: snap(g.x), y: snap(g.y), width: snap(g.width), height: snap(g.height),
                        z_index: maxZ + 1
                    });
                }
            } else if (interaction.kind === 'connect') {
                const target = (document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null)
                    ?.closest<HTMLElement>('[data-beat-id]')?.dataset.beatId;
                if (target && target !== interaction.fromCardId) {
                    createBeatLink(interaction.fromCardId, target);
                }
            }
            setInteraction(null);
            setDraft(null);
            setConnectPointer(null);
            setHoveredLane(null);
            setOutlineDrop(null);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [interaction, view.zoom, toWorld, selectCard, saveBoardItem, createBeatLink, maxZ, placeCard, updateInstance, currentIdea?.outline_target_pages]);

    // Delete removes the selected arrow; Escape clears it
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;
            if (target.closest('input, textarea, [contenteditable="true"]')) return;
            if (selectedLinkId && canEdit && (event.key === 'Delete' || event.key === 'Backspace')) {
                event.preventDefault();
                deleteBeatLink(selectedLinkId);
                setSelectedLinkId(null);
            } else if (event.key === 'Escape') {
                setSelectedLinkId(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedLinkId, canEdit, deleteBeatLink]);

    const startPan = (event: React.PointerEvent) => {
        if (event.button !== 0) return;
        movedRef.current = false;
        setInteraction({ kind: 'pan', start: { x: event.clientX, y: event.clientY }, origin: view });
    };

    const createBeatAt = async (event: React.MouseEvent) => {
        if (!canEdit) return;
        const at = toWorld(event.clientX, event.clientY);
        const card = await createCard({ title: '' });
        if (card) {
            await saveBoardItem(card.id, {
                x: snap(at.x - DEFAULT_SIZE.width / 2), y: snap(at.y - DEFAULT_SIZE.height / 2),
                ...DEFAULT_SIZE, z_index: maxZ + 1
            });
        }
    };

    const startCardMove = (event: React.PointerEvent, card: IdeaCard) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        selectCard(card.id);
        setSelectedLinkId(null);
        const origin = geometryOf(card.id);
        if (!canEdit || !origin) return;
        movedRef.current = false;
        setInteraction({ kind: 'move', cardId: card.id, start: { x: event.clientX, y: event.clientY }, origin });
    };

    const startResize = (event: React.PointerEvent, cardId: string) => {
        event.stopPropagation();
        const origin = geometryOf(cardId);
        if (!origin) return;
        setInteraction({ kind: 'resize', cardId, start: { x: event.clientX, y: event.clientY }, origin });
    };

    const startConnect = (event: React.PointerEvent, cardId: string) => {
        event.stopPropagation();
        setConnectPointer(toWorld(event.clientX, event.clientY));
        setInteraction({ kind: 'connect', fromCardId: cardId });
    };

    const selectedCard = cards.find(c => c.id === selectedCardId) || null;
    const selectedGeometry = selectedCard ? geometryOf(selectedCard.id) : undefined;
    const connectSource = interaction?.kind === 'connect' ? geometryOf(interaction.fromCardId) : undefined;

    return (
        <div
            ref={containerRef}
            className="absolute inset-0 overflow-hidden bg-onyx touch-none"
            style={{
                cursor: interaction?.kind === 'pan' ? 'grabbing' : 'default',
                backgroundImage: 'linear-gradient(to right, color-mix(in oklab, var(--color-iron) 35%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklab, var(--color-iron) 35%, transparent) 1px, transparent 1px)',
                backgroundSize: `${GRID * 2 * view.zoom}px ${GRID * 2 * view.zoom}px`,
                backgroundPosition: `${view.x}px ${view.y}px`
            }}
            onPointerDown={startPan}
            onDoubleClick={createBeatAt}
        >
            {/* World: everything below is in board coordinates */}
            <div
                className="absolute left-0 top-0 origin-top-left"
                style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
            >
                {/* Connectors */}
                <svg className="absolute left-0 top-0 overflow-visible" width={1} height={1}>
                    <defs>
                        {(['idle', 'active'] as const).map(state => (
                            <marker key={state} id={`beat-arrow-${state}`} viewBox="0 0 10 10" refX="9" refY="5"
                                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                                <path d="M0,0 L10,5 L0,10 z" style={{ fill: state === 'active' ? 'var(--color-slate-blue)' : 'var(--color-gray-400)' }} />
                            </marker>
                        ))}
                    </defs>
                    {beatLinks.map(link => {
                        const from = geometryOf(link.from_card_id);
                        const to = geometryOf(link.to_card_id);
                        if (!from || !to) return null;
                        const a = edgePoint(from, center(to));
                        const b = edgePoint(to, center(from));
                        const isSelected = link.id === selectedLinkId;
                        const marker = `url(#beat-arrow-${isSelected ? 'active' : 'idle'})`;
                        return (
                            <g key={link.id}>
                                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} strokeWidth={2}
                                    style={{ stroke: link.color ?? (isSelected ? 'var(--color-slate-blue)' : 'var(--color-gray-400)') }}
                                    markerStart={link.start_cap === 'arrow' ? marker : undefined}
                                    markerEnd={link.end_cap === 'arrow' ? marker : undefined} />
                                {/* Wide invisible hit area */}
                                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="transparent" strokeWidth={14}
                                    className="cursor-pointer" pointerEvents="stroke"
                                    onPointerDown={(event) => {
                                        event.stopPropagation();
                                        setSelectedLinkId(link.id);
                                        selectCard(null);
                                    }} />
                            </g>
                        );
                    })}
                    {connectSource && connectPointer && (
                        <line {...(() => { const a = edgePoint(connectSource, connectPointer); return { x1: a.x, y1: a.y }; })()}
                            x2={connectPointer.x} y2={connectPointer.y} strokeWidth={2} strokeDasharray="6 4"
                            style={{ stroke: 'var(--color-slate-blue)' }} markerEnd="url(#beat-arrow-active)" />
                    )}
                </svg>

                {/* Beats */}
                {orderedCards.map(card => {
                    const g = geometryOf(card.id);
                    if (!g) return null;
                    return (
                        <BeatCard
                            key={card.id}
                            card={card}
                            geometry={g}
                            isSelected={card.id === selectedCardId}
                            canEdit={canEdit}
                            isConnecting={interaction?.kind === 'connect'}
                            onPointerDown={(event) => startCardMove(event, card)}
                            onDoubleClick={(event) => {
                                event.stopPropagation();
                                onOpenCard(card.id);
                            }}
                            onResizeStart={(event) => startResize(event, card.id)}
                            onConnectStart={(event) => startConnect(event, card.id)}
                        />
                    );
                })}

                {/* Selected arrow: delete affordance at its midpoint */}
                {canEdit && selectedLinkId && (() => {
                    const link = beatLinks.find(l => l.id === selectedLinkId);
                    const from = link && geometryOf(link.from_card_id);
                    const to = link && geometryOf(link.to_card_id);
                    if (!link || !from || !to) return null;
                    const a = center(from);
                    const b = center(to);
                    return (
                        <button
                            className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center bg-zinc-900 border border-slate-blue/60 text-slate-blue-soft hover:bg-graphite shadow-lg"
                            style={{ left: (a.x + b.x) / 2, top: (a.y + b.y) / 2 }}
                            title="Delete arrow (Del)"
                            onPointerDown={(event) => event.stopPropagation()}
                            onDoubleClick={(event) => event.stopPropagation()}
                            onClick={() => {
                                deleteBeatLink(link.id);
                                setSelectedLinkId(null);
                            }}
                        >
                            <X className="w-3 h-3" weight="bold" />
                        </button>
                    );
                })()}

                {/* Selected beat: color + kind, only while selected */}
                {canEdit && selectedCard && selectedGeometry && !interaction && (
                    <BeatToolbar
                        card={selectedCard}
                        // Above the beat, or below it when the top would be off-screen
                        style={{
                            left: selectedGeometry.x,
                            top: selectedGeometry.y * view.zoom + view.y < 40 * view.zoom
                                ? selectedGeometry.y + selectedGeometry.height + 8
                                : selectedGeometry.y - 38
                        }}
                        onColor={(color) => updateCard(selectedCard.id, { color })}
                        onKind={(kind) => updateCard(selectedCard.id, { kind })}
                    />
                )}
            </div>

            {/* Teaching empty state */}
            {cards.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center">
                        <p className="text-sm text-neutral-200">{canEdit ? 'Double-click anywhere to add a beat' : 'No beats yet'}</p>
                        {canEdit && <p className="text-xs text-gray-500 mt-1">Drag the dot on a beat's edge to another beat to connect them</p>}
                    </div>
                </div>
            )}

            {/* Where a beat dragged off the board will land on the outline */}
            {outlineDrop && createPortal(
                <div
                    className="fixed z-[200] pointer-events-none px-2 py-1 rounded bg-slate-blue text-white text-[11px] font-medium shadow-xl whitespace-nowrap"
                    style={{ left: outlineDrop.pointer.x + 14, top: outlineDrop.pointer.y + 14 }}
                >
                    → {outlineDrop.lane === 'macro' ? 'Acts' : 'Beats'} · p{formatPage(outlineDrop.page)}
                </div>,
                document.body
            )}

            {/* Zoom controls */}
            <div
                className="absolute bottom-3 right-3 flex items-center gap-0.5 p-0.5 rounded-md bg-zinc-900/90 border border-iron/40 shadow-lg"
                onPointerDown={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
            >
                <button className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-neutral-200 hover:bg-graphite" title="Zoom out" onClick={() => zoomAt(view.zoom / 1.2)}>
                    <Minus className="w-3 h-3" />
                </button>
                <button className="px-1.5 h-6 min-w-[44px] rounded text-[10px] font-medium text-gray-400 hover:text-neutral-200 hover:bg-graphite" title="Reset to 100%" onClick={() => zoomAt(1)}>
                    {Math.round(view.zoom * 100)}%
                </button>
                <button className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-neutral-200 hover:bg-graphite" title="Zoom in" onClick={() => zoomAt(view.zoom * 1.2)}>
                    <Plus className="w-3 h-3" />
                </button>
                <button className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-neutral-200 hover:bg-graphite" title="Fit all beats" onClick={fitToBeats}>
                    <CornersOut className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BEAT CARD - flat fill, bold title, body, Entity / Place / Thing chips
// ═══════════════════════════════════════════════════════════════════════════════

const ENTITY_ICONS: Record<EntityType, typeof User> = { subject: User, location: MapPin, object: Package };

const ENTITY_CHIP_CLASSES: Record<EntityType, string> = {
    subject: 'text-entity-soft bg-entity/10',
    location: 'text-place-soft bg-place/10',
    object: 'text-thing-soft bg-thing/10'
};

interface BeatCardProps {
    card: IdeaCard;
    geometry: BoardGeometry;
    isSelected: boolean;
    canEdit: boolean;
    isConnecting: boolean;
    onPointerDown: (event: React.PointerEvent) => void;
    onDoubleClick: (event: React.MouseEvent) => void;
    onResizeStart: (event: React.PointerEvent) => void;
    onConnectStart: (event: React.PointerEvent) => void;
}

const BeatCard: React.FC<BeatCardProps> = ({
    card, geometry, isSelected, canEdit, isConnecting,
    onPointerDown, onDoubleClick, onResizeStart, onConnectStart
}) => {
    // Entities dragged from the dock attach to the beat (handled by the workspace DndContext)
    const { setNodeRef, isOver } = useDroppable({
        id: `beat-drop-${card.id}`,
        data: { type: 'card-drop', cardId: card.id },
        disabled: !canEdit
    });

    const fill = card.color || null;
    const textColor = fill ? readableTextOn(fill) : undefined;
    const isStructure = card.kind === 'structure';

    return (
        <div
            ref={setNodeRef}
            data-beat-id={card.id}
            className={`absolute group select-none rounded-[3px] ${fill ? '' : 'bg-carbon text-neutral-200'} ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
                ${isSelected ? 'ring-2 ring-slate-blue ring-offset-1 ring-offset-onyx' : ''}
                ${isOver ? 'ring-2 ring-entity' : ''}
                ${isConnecting ? 'hover:ring-2 hover:ring-slate-blue/70' : ''}`}
            style={{
                left: geometry.x,
                top: geometry.y,
                width: geometry.width,
                height: geometry.height,
                background: fill ?? undefined,
                color: textColor,
                // Structure points get Final Draft's heavier border
                border: isStructure
                    ? `3px solid ${fill ? 'color-mix(in oklab, currentColor 55%, transparent)' : 'var(--color-dust)'}`
                    : fill ? 'none' : '1px solid color-mix(in oklab, var(--color-iron) 60%, transparent)'
            }}
            onPointerDown={onPointerDown}
            onDoubleClick={onDoubleClick}
            title={canEdit ? 'Drag to move · double-click to open' : 'Double-click to open'}
        >
            <div className="h-full flex flex-col p-2.5 overflow-hidden">
                <div className="text-[13px] font-semibold leading-tight truncate">
                    {card.title || <span className="opacity-40">Beat Title</span>}
                </div>
                <div className="mt-1 text-[11px] leading-snug opacity-80 overflow-hidden flex-1 whitespace-pre-wrap break-words">
                    {card.content || <span className="opacity-60">Body</span>}
                </div>
                {card.entities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5 max-h-[38px] overflow-hidden">
                        {card.entities.map(entity => {
                            const EntityIcon = ENTITY_ICONS[entity.type];
                            return (
                                <span
                                    key={entity.id}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium max-w-full ${fill ? 'bg-black/15' : ENTITY_CHIP_CLASSES[entity.type]}`}
                                >
                                    <EntityIcon className="w-2.5 h-2.5 flex-shrink-0" weight="fill" />
                                    <span className="truncate">{entity.name}</span>
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            {canEdit && (
                <>
                    {/* Connect handle: drag to another beat */}
                    <div
                        className={`absolute top-1/2 -right-[7px] -translate-y-1/2 w-3 h-3 rounded-full bg-slate-blue border-2 border-onyx cursor-crosshair transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                        title="Drag to another beat to connect"
                        onPointerDown={onConnectStart}
                    />
                    {/* Resize handle */}
                    <div
                        className={`absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize transition-opacity ${isSelected ? 'opacity-70' : 'opacity-0 group-hover:opacity-50'}`}
                        style={{ background: 'linear-gradient(135deg, transparent 50%, currentColor 50%)' }}
                        title="Drag to resize"
                        onPointerDown={onResizeStart}
                    />
                </>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BEAT TOOLBAR - shown above the selected beat only
// ═══════════════════════════════════════════════════════════════════════════════

interface BeatToolbarProps {
    card: IdeaCard;
    style: React.CSSProperties;
    onColor: (color: string | null) => void;
    onKind: (kind: BeatKind) => void;
}

const BeatToolbar: React.FC<BeatToolbarProps> = ({ card, style, onColor, onKind }) => {
    const kind = card.kind ?? 'beat';
    return (
        <div
            className="absolute flex items-center gap-1.5 px-1.5 py-1 rounded-md bg-zinc-900 border border-iron/50 shadow-xl whitespace-nowrap"
            style={style}
            onPointerDown={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
        >
            <div className="flex items-center gap-0.5 p-0.5 rounded bg-onyx border border-iron/40">
                {(['beat', 'structure'] as BeatKind[]).map(option => (
                    <button
                        key={option}
                        onClick={() => onKind(option)}
                        className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${kind === option ? 'bg-graphite text-neutral-200' : 'text-gray-500 hover:text-gray-400'}`}
                        title={option === 'beat' ? 'Beat: any story moment' : 'Structure point: act, sequence or milestone'}
                    >
                        {option === 'beat' ? 'Beat' : 'Structure'}
                    </button>
                ))}
            </div>
            <div className="w-px h-4 bg-iron/50" />
            <div className="flex items-center gap-1">
                {BEAT_COLORS.map(color => (
                    <button
                        key={color}
                        onClick={() => onColor(color)}
                        className={`w-4 h-4 rounded-sm transition-transform hover:scale-110 ${card.color?.toUpperCase() === color ? 'ring-2 ring-slate-blue ring-offset-1 ring-offset-zinc-900' : ''}`}
                        style={{ background: color }}
                        title={color}
                    />
                ))}
                <button
                    onClick={() => onColor(null)}
                    className={`w-4 h-4 rounded-sm flex items-center justify-center border border-iron/60 text-gray-500 hover:text-gray-400 ${!card.color ? 'ring-2 ring-slate-blue ring-offset-1 ring-offset-zinc-900' : ''}`}
                    title="No color"
                >
                    <Prohibit className="w-2.5 h-2.5" />
                </button>
            </div>
        </div>
    );
};
